import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { EnrollmentStatusEnum } from '../../enrollments';
import { useOrganization } from '../../hooks/useOrganizations';
import { useAuthUser } from '../../hooks/useUser';
import { logError } from '../../rollbar';
import { featureFlags } from './featureFlags';
import { fetchInterestedFamilies, sendEnrollmentStatus as sendEnrollmentStatusAPI } from './marketplaceAPI';
import { featureFlagsFetched, headlessTokensCreated, interestedFamiliesFetched } from './marketplaceRedux';
import { exchangeToken } from './token';
import { InterestedFamilyType } from './types';

// Move selectors outside component scope for better memoization
const selectInterestedFamilies = createSelector(
  [(state) => state.marketplace?.interestedFamilies],
  (interestedFamilies) => interestedFamilies
);

const selectMarketplaceFeatures = createSelector(
  [(state) => state.marketplace?.featuresEnabled],
  (featuresEnabled) => featuresEnabled
);

const selectMarketplaceHeadlessConfig = createSelector(
  [(state) => state.marketplace?.headless],
  (headless) => headless
);

export function useMarketplaceAPI() {
  const organization = useOrganization();
  const { currentUser } = useAuthUser();
  const [token, setToken] = useState<string>();
  const { marketplaceSchoolId } = organization;

  // Memoize token fetching callback
  const fetchToken = useCallback(async () => {
    if (currentUser) {
      const idTokenResult = await currentUser.getIdTokenResult();
      setToken(idTokenResult.token);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!token) {
      fetchToken();
    }
  }, [fetchToken, token]);

  const sendEnrollmentStatus = useCallback(
    async (enrollmentStatus: EnrollmentStatusEnum, marketplaceLeadId: string): Promise<any> => {
      if (token && marketplaceSchoolId) {
        return sendEnrollmentStatusAPI({ organization, token, enrollmentStatus, marketplaceLeadId });
      }
    },
    [marketplaceSchoolId, organization, token]
  );

  return { token, marketplaceSchoolId, organization, sendEnrollmentStatus };
}

export function useInterestedFamilies(): InterestedFamilyType[] {
  const { token, marketplaceSchoolId, organization } = useMarketplaceAPI();
  const interestedFamilies = useSelector(selectInterestedFamilies);
  const dispatch = useDispatch();

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const fetchFamilies = async () => {
      try {
        if (token && marketplaceSchoolId && !interestedFamilies?.fetched) {
          const families = await fetchInterestedFamilies({
            organization,
            token,
          });
          if (mounted) {
            dispatch(interestedFamiliesFetched(families));
          }
        }
      } catch (e) {
        if (mounted) {
          logError(e);
        }
      }
    };

    fetchFamilies();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [dispatch, interestedFamilies?.fetched, marketplaceSchoolId, organization, token]);

  return interestedFamilies?.list ?? [];
}

export function useMarketplaceHeadless() {
  const { token, marketplaceSchoolId } = useMarketplaceAPI();
  const dispatch = useDispatch();
  const headless = useSelector(selectMarketplaceHeadlessConfig);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const exchangeTokens = async () => {
      try {
        const anyTokenNull = !headless.refreshToken || !headless.accessToken;
        if (token && marketplaceSchoolId && anyTokenNull) {
          const tokens = await exchangeToken(token);
          if (mounted) {
            dispatch(headlessTokensCreated(tokens));
          }
        }
      } catch (e) {
        if (mounted) {
          logError(e);
        }
      }
    };

    exchangeTokens();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [dispatch, headless.refreshToken, headless.accessToken, marketplaceSchoolId, token]);

  return headless;
}

export function useMarketplaceFeaturesEnabled() {
  const { token, marketplaceSchoolId } = useMarketplaceAPI();
  const dispatch = useDispatch();
  const featuresEnabled = useSelector(selectMarketplaceFeatures);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const fetchFeatures = async () => {
      try {
        if (token && marketplaceSchoolId && !featuresEnabled?.fetched) {
          const ff = await featureFlags(marketplaceSchoolId);
          if (mounted) {
            dispatch(featureFlagsFetched(ff));
          }
        }
      } catch (e) {
        if (mounted) {
          logError(e);
        }
      }
    };

    fetchFeatures();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [dispatch, featuresEnabled?.fetched, marketplaceSchoolId, token]);

  return featuresEnabled;
}
